import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NameEditorModal, useXlsImporterSettings } from "@tools";
import { Alert, ClearButton, InteractiveAutocomplete } from "@components";
import { CircularProgress, Grid } from "@material-ui/core";
import { Add, Save } from "@material-ui/icons";

export const FastModeConfigurations = () => {
  
  const { t } = useTranslation();
  const settings = useXlsImporterSettings();
  
  const [ isCreating, setIsCreating ] = useState(false);
  const [ isSaving, setIsSaving ] = useState(false);
  const [ modalVisible, showModal ] = useState(false);
  const [ removedId, setRemovedId ] = useState(false);
  
  const {
    usedConfiguration,
    defaultConfiguration,
    importConfigurations,
  } = settings;
  
  const handleSelectConfig = (selectedConfigData) => {
    settings.selectImportConfiguration(selectedConfigData);
  }
  
  const handleCreateConfig = async (newName) => {
    try {
      if (newName) {
        setIsCreating(true);
        const existingConfig = importConfigurations.find(c => c.name === newName);
        if ( existingConfig )
          await settings.updateImportConfiguration(existingConfig.id);
        else
          await settings.createImportConfiguration(newName)
        setIsCreating(false);
      }
      showModal(false);
    } catch(e) {
      showModal(false);
    }
  }
  
  const handleSaveConfig = async () => {
    setIsSaving(true);
    await settings.updateImportConfiguration()
    setIsSaving(false);
  }
  
  const handleDefaultConfig = async (_, id) => {
    await settings.setDefaultImportConfiguration(id === defaultConfiguration ? null : id);
  }
  
  const handleRemoveConfigAlert = async (_, id) => {
    setRemovedId(id);
  }
  
  const handleRemoveConfig = async () => {
    if(typeof removedId !== "number")
      return;
    await settings.removeImportConfiguration(removedId);
    setRemovedId(null);
  }
  
  return (
    <Grid
      item container
      wrap="nowrap"
      alignItems="center"
      className="w-max"
    >
      <span className="text-md font-bold">
        {t("views.import.configs.title")}{":"}
      </span>
      <InteractiveAutocomplete
        id="xlsConfig"
        value={usedConfiguration}
        options={importConfigurations}
        defaultOptionId={defaultConfiguration}
        idKey="id"
        labelKey="name"
        containerClassName="mx-2"
        inputLabel={t("views.import.configs.placeholder")}
        noOptionsText={t("views.import.configs.empty")}
        optionClassName="font-normal pl-4 justify-between"
        onChange={handleSelectConfig}
        onOptionRemove={handleRemoveConfigAlert}
        onSetDefaultOption={handleDefaultConfig}
        size="small"
        width={212}
      />
      {
        usedConfiguration && <ClearButton
          size="medium"
          endIcon={isSaving ? <CircularProgress size={18} /> : <Save />}
          onClick={handleSaveConfig}
          className="w-208"
          disabled={isCreating}
        >
          { isSaving ? t("views.import.configs.saving") : t("views.import.configs.save") }
        </ClearButton>
      }
      <ClearButton
        size="medium"
        endIcon={isCreating ? <CircularProgress size={18} /> : <Add />}
        onClick={() => showModal(true)}
        className="w-max"
        disabled={isSaving}
      >
        {t("views.import.configs.create") }
      </ClearButton>
      
      <NameEditorModal
        open={modalVisible}
        onClose={handleCreateConfig}
        title={t("views.import.configs.name")}
        confirmButtonTitle={t("views.import.configs.create")}
        width={360}
      />
      <Alert
        isOpen={typeof removedId === "number"}
        onAccept={handleRemoveConfig}
        onCancel={handleRemoveConfig}
        title={t("views.import.configs.delete_question")}
        acceptText={t("common.delete_accept")}
      />
    </Grid>
  )
}